import {
  Avatar,
  Badge,
  Button,
  cx,
  Dropdown,
  Tooltip,
} from "@light-technology/components";
import {
  ChevronSelectorVertical,
  Cube01,
  Edit01,
  Plus,
} from "@untitledui/icons";
import { useState } from "react";
import { MenuItem, Pressable } from "react-aria-components";
import { useNavigate } from "react-router";
import { Role, type AppResponse } from "../state/api.types";
import { useAuthHasRole } from "../state/hooks";
import { local } from "../state/local";
import { useTypedDispatch } from "../state/store";
import { CreateAppModal } from "./CreateAppModal";
import { RoleGate } from "./RoleGate";
import { UpdateAppModal } from "./UpdateAppModal";

type AppLayoutSwitcherProps = {
  app: AppResponse;
  apps: AppResponse[];
  className?: string;
};

export const AppLayoutSwitcher = (props: AppLayoutSwitcherProps) => {
  const navigate = useNavigate();
  const dispatch = useTypedDispatch();

  const [isCreate, setIsCreate] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);

  const hasPermission = useAuthHasRole(Role.Developer, Role.OrgAdmin);

  return (
    <>
      <Dropdown.Root>
        <Tooltip title={`Switch from ${props.app.name} App`} arrow>
          <Pressable>
            <div
              role="button"
              className={cx([
                "flex cursor-pointer items-center gap-md rounded-xl border border-secondary p-lg hover:border-primary",
                props.className,
              ])}
            >
              <Avatar
                className="flex-none"
                size="lg"
                placeholderIcon={Cube01}
                src={props.app.configuration?.favicon_logo}
              />
              <div className="min-w-0 flex-auto">
                <div className="truncate text-sm font-semibold text-primary">
                  {props.app.name} App
                </div>
                <div className="truncate font-mono text-sm text-tertiary">
                  {props.app.slug}
                </div>
              </div>
              <ChevronSelectorVertical className="size-4 flex-none text-fg-quaternary" />
            </div>
          </Pressable>
        </Tooltip>
        <Dropdown.Popover className="w-(--trigger-width)" placement="bottom">
          <div className="px-lg pt-lg text-xs font-semibold text-tertiary">
            Switch app
          </div>
          <Dropdown.Menu
            selectedKeys={[props.app.slug]}
            onAction={(appSlug) => {
              if (appSlug !== props.app.slug) {
                dispatch(local.actions.set({ appSlug: appSlug as string }));
                // Navigate to the active sub-path after /apps/:slug/, not deeper.
                // Get the pathname after the slug - `/apps/:slug/...`
                const pathParts = location.pathname.split("/");
                const fallbackPath = hasPermission ? "" : "accounts";
                const afterSlug =
                  pathParts.length > 3 ? pathParts[3] : fallbackPath;
                navigate(`/apps/${appSlug as string}/${afterSlug}`);
              }
            }}
          >
            {props.apps.map((app) => (
              <Dropdown.Item key={app.slug} id={app.slug}>
                <div className="flex items-center gap-md">
                  <Avatar
                    className="flex-none"
                    size="md"
                    placeholderIcon={Cube01}
                    src={app.configuration?.favicon_logo}
                  />
                  <div className="min-w-0 flex-auto">
                    <div className="truncate text-sm font-semibold text-primary">
                      {app.name}
                    </div>
                    <div className="truncate font-mono text-sm text-tertiary">
                      {app.slug}
                    </div>
                  </div>
                  {app.is_sandbox && (
                    <Badge size="sm" type="modern">
                      Sandbox
                    </Badge>
                  )}
                </div>
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
          <RoleGate roles={[Role.OrgAdmin, Role.Developer]}>
            <Dropdown.Menu className="space-y-xs p-md">
              <MenuItem>
                <Button
                  className="w-full"
                  color="secondary"
                  size="sm"
                  iconLeading={Plus}
                  onClick={() => {
                    setIsCreate(true);
                  }}
                >
                  Create new app
                </Button>
              </MenuItem>
              <MenuItem>
                <Button
                  className="w-full"
                  color="tertiary"
                  size="sm"
                  iconLeading={Edit01}
                  onClick={() => {
                    setIsUpdate(true);
                  }}
                >
                  Edit app name
                </Button>
              </MenuItem>
            </Dropdown.Menu>
          </RoleGate>
        </Dropdown.Popover>
      </Dropdown.Root>
      <CreateAppModal isOpen={isCreate} setIsOpen={setIsCreate} />
      <UpdateAppModal
        app={props.app}
        isOpen={isUpdate}
        setIsOpen={setIsUpdate}
      />
    </>
  );
};
